import { createContext, useMemo } from 'react';

import { useContextFallback } from '../../shared/hooks';
import { ResourceCount } from './common/models';
import { useFavouritesCountQuery } from './favourites/queries';
import { CopyFolderProvider, FolderTreeProvider } from './folders/context';
import { usePendingApprovalCountQuery } from './pending-approval/queries';

type ResourcesContextState = {
  pendingApproval: ResourceCount;
  favourites: ResourceCount;
};

const ResourcesContext = createContext<ResourcesContextState | undefined>(
  undefined
);
ResourcesContext.displayName = 'ResourcesContext';

export const ResourcesProvider: React.FC = ({ children }) => {
  const favouritesCountQuery = useFavouritesCountQuery();
  const pendingApprovalCountQuery = usePendingApprovalCountQuery();

  const value: ResourcesContextState = useMemo(
    () => ({
      pendingApproval: {
        isLoading: pendingApprovalCountQuery.isLoading,
        noCount:
          pendingApprovalCountQuery.isError || !pendingApprovalCountQuery.data,
        count: pendingApprovalCountQuery.data?.count,
      },
      favourites: {
        isLoading: favouritesCountQuery.isLoading,
        noCount: favouritesCountQuery.isError || !favouritesCountQuery.data,
        count: favouritesCountQuery.data?.count,
        refetch: favouritesCountQuery.refetch,
      },
    }),
    [pendingApprovalCountQuery, favouritesCountQuery]
  );

  return (
    <ResourcesContext.Provider value={value}>
      <FolderTreeProvider>
        <CopyFolderProvider>{children}</CopyFolderProvider>
      </FolderTreeProvider>
    </ResourcesContext.Provider>
  );
};

export const useResources = () => useContextFallback(ResourcesContext);
